import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import {
  Box,
  Chip,
  Button,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { t } from 'i18next';

import { domainRegex } from '@common/schema';
import { theme } from '@store';
import {
  CustomModal,
  FormGooglePlacesAutocomplete,
  SaveChangesModal,
} from '@components';

import { useCreateOrganization } from './hooks';
import { USStateSelect } from 'components/USStateSelect';

export const CreateOrganization = ({
  handleOpenOrganizationDetails,
}: {
  handleOpenOrganizationDetails: (id: string) => void;
}) => {
  const {
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    handleAddDomain,
    handleRemoveDomain,
    handleSelectLocation,
    formMethods,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useCreateOrganization(handleOpenOrganizationDetails);

  const { control } = formMethods;

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
        color='secondary'
        variant='contained'
        onClick={handleOnClick}
      >
        {t('adminPanel.organizations.addModal.addOrganization')}
      </Button>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('adminPanel.organizations.addModal.title')}
        isLoading={isSubmitting}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Controller
              name='name'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.organizations.addModal.name')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  sx={{ paddingBottom: '0.5rem' }}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name='tin'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.organizations.addModal.tin')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />
            <Controller
              name='guide_participant_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t(
                    'adminPanel.organizations.addModal.guide_participant_id',
                  )}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />
            <Box sx={{ width: '100%' }}>
              <Controller
                name='domainGenerator'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const isAddDisabled = !domainRegex.test(field.value || '');

                  return (
                    <FormControl fullWidth variant='outlined'>
                      <InputLabel
                        htmlFor={'domainGenerator'}
                        error={!isNil(error)}
                        required
                      >
                        {t(
                          'adminPanel.organizations.addModal.organizationEmailDomains',
                        )}
                      </InputLabel>
                      <OutlinedInput
                        {...field}
                        type='text'
                        label='Organization email domains'
                        error={!isNil(error)}
                        required
                        fullWidth
                        sx={{ color: 'black' }}
                        endAdornment={
                          <InputAdornment position='end'>
                            <Button
                              variant='outlined'
                              sx={{ paddingY: 0 }}
                              onClick={() => handleAddDomain(field.value)}
                              disabled={isAddDisabled}
                            >
                              CONFIRM
                            </Button>
                          </InputAdornment>
                        }
                      />
                      {!!error?.message && (
                        <FormHelperText error id='domainGenerator-error'>
                          {error.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />
              <Controller
                name='domains'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormControl fullWidth sx={{ padding: 0 }}>
                      <Box
                        sx={{
                          overflow: 'auto',
                          maxHeight: '3rem',
                          height: 'fit-content',
                          pt: '0.5rem',
                        }}
                      >
                        {(field.value ?? []).map((domain, index) => (
                          <Chip
                            key={`${domain}-${index}`}
                            label={`@${domain}`}
                            onDelete={() => handleRemoveDomain(index)}
                            sx={{
                              borderRadius: '1rem',
                              mx: '0.25rem',
                              mb: '0.5rem',
                            }}
                          />
                        ))}
                        {!!error?.message && (
                          <FormHelperText error id='domainGenerator-error'>
                            {error.message}
                          </FormHelperText>
                        )}
                      </Box>
                    </FormControl>
                  );
                }}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                gap: '1rem',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant='subtitle2'
                sx={{
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                {t('adminPanel.organizations.addModal.contactInformation')}
              </Typography>

              <FormGooglePlacesAutocomplete
                required
                onChange={handleSelectLocation}
              />

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  gap: '0.5rem',
                }}
              >
                <Controller
                  name='street'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type='text'
                      label={t(
                        'adminPanel.organizations.addModal.addressLine1',
                      )}
                      variant='outlined'
                      error={!isNil(error)}
                      helperText={error ? error.message : null}
                      required
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name='suite'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type='text'
                      label={t(
                        'adminPanel.organizations.addModal.addressLine2',
                      )}
                      variant='outlined'
                      error={!isNil(error)}
                      helperText={error ? error.message : null}
                      fullWidth
                    />
                  )}
                />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  gap: '0.5rem',
                }}
              >
                <Controller
                  name='city'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type='text'
                      label={t('adminPanel.organizations.addModal.city')}
                      variant='outlined'
                      error={!isNil(error)}
                      helperText={error ? error.message : null}
                      required
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name='state'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <USStateSelect
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ''}
                      required
                    />
                  )}
                />
              </Box>
              <Controller
                name='zip_code'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.organizations.addModal.zip')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
              <Controller
                name='county'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.organizations.addModal.county')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    fullWidth
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={!isValid || isSubmitting}
              >
                {t('general.ADD')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </>
  );
};
